import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Atom, ShieldAlert, Info, Brain, List, Stethoscope, Menu, X, HardHat, Search, Briefcase } from "lucide-react";
import { InstallPWA, OfflineBadge } from "./InstallPWA";

export const Navbar = () => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: "/", testid: "nav-elements", icon: <Atom size={14} strokeWidth={2.5} />, label: "Table" },
    { to: "/index", testid: "nav-index", icon: <List size={14} strokeWidth={2.5} />, label: "Index" },
    { to: "/symptoms", testid: "nav-symptoms", icon: <Stethoscope size={14} strokeWidth={2.5} />, label: "Symptoms" },
    { to: "/diseases", testid: "nav-diseases", icon: <HardHat size={14} strokeWidth={2.5} />, label: "Diseases" },
    { to: "/disease-search", testid: "nav-disease-search", icon: <Search size={14} strokeWidth={2.5} />, label: "Dx Search" },
    { to: "/first-aid-kit", testid: "nav-first-aid-kit", icon: <Briefcase size={14} strokeWidth={2.5} />, label: "Kit" },
    { to: "/quiz", testid: "nav-quiz", icon: <Brain size={14} strokeWidth={2.5} />, label: "Quiz" },
    { to: "/posters", testid: "nav-posters", icon: <ShieldAlert size={14} strokeWidth={2.5} />, label: "Posters" },
    { to: "/about", testid: "nav-about", icon: <Info size={14} strokeWidth={2.5} />, label: "About" },
  ];

  return (
    <nav className="border-b-2 border-zinc-950 bg-white sticky top-0 z-50 print:hidden" data-testid="main-navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="navbar-logo">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
              <img src="/anaga-logo.png" alt="AnagaHealth" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
            </div>
            <div>
              <div className="font-heading font-black text-base sm:text-lg tracking-tighter uppercase leading-none">CHEM/SAFETY</div>
              <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-zinc-600 leading-none mt-0.5 hidden sm:block">
                <span className="text-zinc-950 font-bold">CHEM/SAFETY</span> · 118 elements
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} active={isActive(l.to)} testid={l.testid}>
                {l.icon} {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l-2 border-zinc-200">
              <OfflineBadge />
              <InstallPWA />
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden border-2 border-zinc-950 p-1.5 bg-white"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden border-t-2 border-zinc-950 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2 px-3 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border-2 transition-all ${
                  isActive(l.to)
                    ? "bg-zinc-950 text-white border-zinc-950"
                    : "bg-white text-zinc-950 border-transparent hover:border-zinc-950"
                }`}
              >
                {l.icon} {l.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 px-1 py-2 border-t border-zinc-200 mt-1">
              <OfflineBadge />
              <InstallPWA />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

const NavLink = ({ to, active, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] border-2 transition-all ${
      active
        ? "bg-zinc-950 text-white border-zinc-950"
        : "bg-white text-zinc-950 border-transparent hover:border-zinc-950 hover:neo-shadow"
    }`}
  >
    {children}
  </Link>
);
